import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { AuthProvider } from '@/hooks/auth/AuthProvider';
import { DemoProvider } from '@/contexts/DemoProvider';
import { useDemo } from '@/contexts/useDemo';
import { DemoBanner } from '@/components/demo/DemoBanner';
import ErrorBoundary from '@/components/ErrorBoundary';
import Index from '@/pages/Index';

// Everything except the landing page is lazy: the booking-first bundle
// stays small and each route ships as its own chunk.
const HowItWorks = lazy(() => import('@/pages/HowItWorks'));
const AboutUs = lazy(() => import('@/pages/AboutUs'));
const ContactUs = lazy(() => import('@/pages/ContactUs'));
const Faq = lazy(() => import('@/pages/Faq'));
const Safety = lazy(() => import('@/pages/Safety'));
const Auth = lazy(() => import('@/pages/Auth'));
const Profile = lazy(() => import('@/pages/Profile'));
const Rides = lazy(() => import('@/pages/Rides'));
const RideDetails = lazy(() => import('@/pages/RideDetails'));
const MyDrivers = lazy(() => import('@/pages/MyDrivers'));
const Negotiation = lazy(() => import('@/pages/Negotiation'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const DriverDashboard = lazy(() => import('@/pages/DriverDashboard'));
const DriverSignup = lazy(() => import('@/pages/DriverSignup'));
const DemoLanding = lazy(() => import('@/pages/DemoLanding'));

const RouteFallback = () => (
  <div className="flex justify-center items-center min-h-screen" role="status" aria-live="polite">
    Loading…
  </div>
);

/**
 * Remounts the routed tree when the demo is reset (and, from Stage 3, when the
 * role switches). Keyed inside the Router so the URL survives while the page
 * subtree does not — several pages seed local state from a one-shot mount
 * effect and would otherwise show the previous session's data.
 */
const DemoBoundary = ({ children }: { children: React.ReactNode }) => {
  const { role, resetNonce } = useDemo();
  return <React.Fragment key={`${role ?? 'live'}:${resetNonce}`}>{children}</React.Fragment>;
};

const App = () => {
  return (
    <ErrorBoundary>
      <DemoProvider>
        <AuthProvider>
          <Router>
            <DemoBanner />
            <a
              href="#main-content"
              className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[200] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:text-sm focus:font-medium"
            >
              Skip to main content
            </a>
            <DemoBoundary>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/demo" element={<DemoLanding />} />
                  <Route path="/" element={<Index />} />
                  <Route path="/how-it-works" element={<HowItWorks />} />
                  <Route path="/about-us" element={<AboutUs />} />
                  <Route path="/contact-us" element={<ContactUs />} />
                  <Route path="/faq" element={<Faq />} />
                  <Route path="/safety" element={<Safety />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/rides" element={<Rides />} />
                  <Route path="/my-drivers" element={<MyDrivers />} />
                  <Route path="/rides/:id" element={<RideDetails />} />
                  <Route path="/negotiations/:id" element={<Negotiation />} />
                  <Route path="/driver-signup" element={<DriverSignup />} />
                  <Route path="/driver-dashboard" element={<DriverDashboard />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </DemoBoundary>
          </Router>
        </AuthProvider>
      </DemoProvider>
    </ErrorBoundary>
  );
};

export default App;
